/* eslint-disable max-len */

import dayjs, { Dayjs } from 'dayjs';
import DateDiff from 'date-diff';
import { isNullOrUndefined, capitalize } from './lang';

const LONG_DISPLAY_DATE_FORMAT = 'dddd D. MMMM YYYY';
const SANITY_TRANSPORT_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const SANITY_TRANSPORT_FORMAT_DATE_ONLY = 'YYYY-MM-DD';
const SANITY_GENERATED_FORMAT = 'YYYY-MM-DDTHH:mm:SSZ';

/**
 * Returns the number of days until the date occurrs or since it has occurred (negative number).
 * @param {Date} date - The date object we want to calculate days to from now.
 */
export const inDays = (date: Date): number => {
  const now = new Date();
  const d = new DateDiff(date, now);
  return Math.round(d.days());
};

/**
 * Returns the number of months until the date occurrs or since it has occurred (negative number).
 * @param {Date} date - The date object we want to calculate days to from now.
 */
export const inMonths = (date: Date): number => {
  const now = new Date();
  const d = new DateDiff(date, now);
  return Math.round(d.months());
};

/**
 * Formats a parsed Dayjs datetime object into a string on the format like "Tirsdag 26. januar 2021".
 * @param {Dayjs} date - The Dayjs date object we want to format to a string
 */
export const formatToLongDisplayFormat = (date: Dayjs): string => (isNullOrUndefined(date)
  ? ''
  : capitalize(date.format(LONG_DISPLAY_DATE_FORMAT)));

/**
 * Parses timestamps for fields auto-generated by sanity. Typically the _createdAt and _updatedAt fields we get when querying for data through Sanity's API.
 * For parsing a `datetime` or `date` field set by a user in Sanity Studio, see `parseDateTimeFromSanityInputField` or `parseDateFromSanityInputField` below.
 * @param {string} date - The string we want to parse to a Dayjs date object.
 */
export const parseDateTimeFromSanityAutoGeneratedTimestamp = (date: string): Dayjs => dayjs(date, SANITY_GENERATED_FORMAT);

/**
 * Parses timestamps for Sanity fields with `type: "datetime"` in the schema and which editors/users can select a date and timestamp in Sanity Studio for a field.
 * If the field type is `date` and not `datetime`, use `parseDateFromSanityInputField` instead.
 * @param {string} date - The string we want to parse to a Dayjs date object.
 */
export const parseDateTimeFromSanityInputField = (date: string): Dayjs => dayjs(date, SANITY_TRANSPORT_FORMAT);

/**
 * Parses timestamps for Sanity fields with `type: "date"` in the schema and which editors/users can select a date in Sanity Studio for a field.
 * If the field type is `datetime` and not `date`, use `parseDateTimeFromSanityInputField` instead.
 * @param {string} date - The string we want to parse to a Dayjs date object.
 */
export const parseDateFromSanityInputField = (date: string): Dayjs => dayjs(date, SANITY_TRANSPORT_FORMAT_DATE_ONLY);
